import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { IconButton, PencilIcon, TrashIcon } from '../../components/ui/icons';
import { formatPhoneInput, isCompletePhone, displayPhone } from '../../lib/phone';

// Barn-wide rider roster — the row axis of the weekly schedule. Schedule staff
// (owner / manager / trainer) only. Contact shown here is "effective": for a
// rider linked to an owner account it comes from the owner; otherwise it's the
// rider's own. Add riders here or inline from the weekly grid.
//
// Arriving with ?new=<name> (e.g. from the Week page's "Add rider") opens the
// add form prefilled with that name.
const SCHED = ['owner', 'manager', 'trainer'];

export default function RidersDirectory() {
  const { activeRole } = useAuth();
  const canSchedule = SCHED.includes(activeRole);
  const qc = useQueryClient();

  const [params, setParams] = useSearchParams();
  const newParam = params.get('new'); // string | null
  const [adding, setAdding] = useState(newParam !== null);
  const [importing, setImporting] = useState(false);

  // Open (and prefill) the form when navigated here with ?new=.
  useEffect(() => {
    if (newParam !== null) setAdding(true);
  }, [newParam]);

  const closeAdd = () => {
    setAdding(false);
    if (newParam !== null) {
      params.delete('new');
      setParams(params, { replace: true });
    }
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['people'],
    queryFn: () => api.get('/people'),
    enabled: canSchedule,
  });

  const remove = useMutation({
    mutationFn: (id) => api.del(`/people/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['people'] }),
  });

  if (!canSchedule) {
    return <div className="p-4 text-ink-500">The rider roster is for barn staff.</div>;
  }
  if (isLoading) return <div className="p-4 text-ink-400">Loading…</div>;
  if (isError) return <div className="p-4 text-red-600">Couldn’t load riders.</div>;

  const people = data?.people ?? [];

  return (
    <div className="max-w-3xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Riders</h1>
        <div className="flex items-center gap-2">
          {!adding && !importing && (
            <button onClick={() => setImporting(true)} className="btn-secondary">
              Add From Owners
            </button>
          )}
          {!adding && !importing && (
            <button onClick={() => setAdding(true)} className="btn-primary">
              Add Rider
            </button>
          )}
        </div>
      </div>

      {adding && <AddRiderForm initialName={newParam ?? ''} onDone={closeAdd} />}
      {importing && <ImportOwners onDone={() => setImporting(false)} />}

      {people.length === 0 && !adding && !importing && (
        <p className="text-ink-400">
          No riders yet. Everyone who owns a horse here is probably one — try
          <button
            type="button"
            className="underline mx-1"
            onClick={() => setImporting(true)}
          >
            Add From Owners
          </button>
          to add them all at once.
        </p>
      )}

      <ul className="space-y-2">
        {people.map((p) => {
          const birthday = formatBirthday(p.birthday);
          const contact =
            [p.email, p.phone ? displayPhone(p.phone) : null].filter(Boolean).join(' · ');
          return (
            <li key={p.id} className="card p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Link
                  to={`/riders/${p.id}`}
                  className="font-medium text-ink-900 hover:underline truncate block"
                >
                  {p.name}
                  {p.linkedToOwner && (
                    <span className="ml-2 align-middle text-xs font-normal px-2 py-0.5 rounded-full bg-ink-100 text-ink-500">
                      Owner
                    </span>
                  )}
                </Link>
                <div className="text-sm text-ink-500 truncate">
                  {contact || <span className="text-ink-300">No contact</span>}
                </div>
                {birthday && <div className="text-sm text-ink-400">Birthday · {birthday}</div>}
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <IconButton as={Link} to={`/riders/${p.id}`} icon={PencilIcon} label="Edit" />
                <IconButton
                  icon={TrashIcon}
                  tone="danger"
                  label="Remove from roster"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (confirm(`Remove ${p.name} from the roster? Their past lessons are kept.`)) {
                      remove.mutate(p.id);
                    }
                  }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AddRiderForm({ initialName = '', onDone }) {
  const qc = useQueryClient();
  // Most riders at an established barn are already horse owners, so retyping
  // their details is wasted work. POST /people has always accepted a userId to
  // link an existing barn member and inherit their name — it just wasn't
  // offered anywhere. Default to that when there's anyone to pick.
  const [mode, setMode] = useState(initialName ? 'new' : 'existing');
  const [linkUserId, setLinkUserId] = useState('');
  const [name, setName] = useState(initialName);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [birthday, setBirthday] = useState('');

  // Horse owners who aren't already riders.
  const ownersQ = useQuery({ queryKey: ['owners'], queryFn: () => api.get('/owners') });
  const peopleQ = useQuery({ queryKey: ['people'], queryFn: () => api.get('/people') });
  const linkedIds = new Set(
    (peopleQ.data?.people ?? []).map((p) => p.userId).filter(Boolean),
  );
  const candidates = (ownersQ.data?.owners ?? []).filter((o) => !linkedIds.has(o.userId));

  const add = useMutation({
    mutationFn: () =>
      api.post(
        '/people',
        mode === 'existing'
          // Name is omitted on purpose: the server takes it from the linked
          // account, so the two can't drift apart later.
          ? { userId: linkUserId }
          : {
              name: name.trim(),
              email: email.trim() || undefined,
              phone: phone.trim() || undefined,
              birthday: birthday || undefined,
            },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['people'] });
      onDone();
    },
  });

  const phoneOk = phone === '' || isCompletePhone(phone);
  const canSubmit =
    (mode === 'existing' ? !!linkUserId : name.trim() && phoneOk) && !add.isPending;

  return (
    <div className="card p-4 mb-4 space-y-2">
      {candidates.length > 0 && (
        <div className="flex gap-1 mb-1">
          {[
            ['existing', 'A horse owner'],
            ['new', 'Someone new'],
          ].map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => setMode(v)}
              className={
                'text-sm px-3 py-1.5 rounded-full ' +
                (mode === v ? 'bg-ink-800 text-white' : 'bg-ink-100 text-ink-600')
              }
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {mode === 'existing' && candidates.length > 0 ? (
        <>
          <select
            className="input"
            value={linkUserId}
            onChange={(e) => setLinkUserId(e.target.value)}
            aria-label="Horse owner to add as a rider"
          >
            <option value="">Choose a horse owner…</option>
            {candidates.map((o) => (
              <option key={o.userId} value={o.userId}>
                {o.name || o.email}
              </option>
            ))}
          </select>
          <p className="text-xs text-ink-400">
            Links to their existing record, so contact details stay in one place.
          </p>
        </>
      ) : (
      <>
      <input
        className="input"
        placeholder="Name"
        value={name}
        maxLength={120}
        autoFocus
        onChange={(e) => setName(e.target.value)}
      />
      <input
        className="input"
        type="email"
        placeholder="Email (optional)"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <div>
        <input
          className="input"
          type="tel"
          inputMode="numeric"
          placeholder="Mobile (optional)"
          value={phone}
          onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
        />
        {!phoneOk && <p className="mt-1 text-xs text-amber-600">Enter a 10-digit phone number.</p>}
      </div>
      <div>
        <label className="block text-xs text-ink-400 mb-1">Birthday (optional)</label>
        <input
          className="input"
          type="date"
          value={birthday}
          onChange={(e) => setBirthday(e.target.value)}
        />
      </div>
      </>
      )}

      {add.isError && <p className="text-sm text-red-600">{friendlyError(add.error)}</p>}

      <div className="flex gap-2 pt-1">
        <button onClick={() => add.mutate()} disabled={!canSubmit} className="btn-primary">
          {add.isPending ? 'Adding…' : 'Add Rider'}
        </button>
        <button onClick={onDone} className="text-sm text-ink-500 hover:text-ink-800 px-2">
          Cancel
        </button>
      </div>
    </div>
  );
}

// Format a stored ISO birthday (YYYY-MM-DD) as "June 15" — month name + day, no
// year. Parses the string directly to avoid Date() timezone shifts.
function formatBirthday(birthday) {
  if (!birthday) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(birthday);
  if (!m) return null;
  const monthIdx = Number(m[2]) - 1;
  const day = Number(m[3]);
  const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  if (monthIdx < 0 || monthIdx > 11 || !day) return null;
  return `${MONTHS[monthIdx]} ${day}`;
}

function friendlyError(err) {
  const code = err?.body?.error || err?.message || '';
  switch (code) {
    case 'already_rider':
      return 'That person is already on the rider roster.';
    case 'user_not_in_barn':
      return 'That person is no longer in this barn.';
    case 'missing_name':
      return 'Name is required.';
    default:
      return 'Could not add rider. Try again.';
  }
}

/**
 * Add the barn's existing members to the rider roster in one go.
 *
 * Westwood imported 28 owners and has no riders, because the add form takes
 * them one at a time and nobody is going to do that twenty-five times. These
 * are the same people the barn already told us about; this just says so.
 *
 * Anyone who owns a horse here is pre-ticked, on the reasoning that they either
 * ride or someone in their household does. Staff appear but start unticked —
 * a groom is not usually on the lesson schedule.
 */
function ImportOwners({ onDone }) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState(null); // null until the list loads
  const [err, setErr] = useState('');

  const candidatesQ = useQuery({
    queryKey: ['people', 'candidates'],
    queryFn: () => api.get('/people/candidates'),
  });

  const candidates = candidatesQ.data?.candidates ?? [];
  // Seed the selection from the server's suggestion the first time only, so a
  // refetch can't silently re-tick something the user deliberately unticked.
  const selected = picked ?? new Set(candidates.filter((c) => c.suggested).map((c) => c.userId));

  const toggle = (userId) => {
    const next = new Set(selected);
    if (next.has(userId)) next.delete(userId);
    else next.add(userId);
    setPicked(next);
  };

  const add = useMutation({
    mutationFn: (userIds) => api.post('/people/bulk', { userIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['people'] });
      onDone();
    },
    onError: (e) => setErr(friendlyBulk(e)),
  });

  if (candidatesQ.isLoading) {
    return <div className="card p-4 mb-4 text-ink-400 text-sm">Loading…</div>;
  }

  if (candidates.length === 0) {
    return (
      <div className="card p-4 mb-4">
        <p className="text-sm text-ink-500">
          Everyone in this barn is already on the roster.
        </p>
        <button type="button" className="btn-secondary text-sm mt-3" onClick={onDone}>
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="card p-4 mb-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="font-medium">Add from owners</h2>
          <p className="text-sm text-ink-500 mt-1">
            People already in this barn who aren't on the rider roster. Adding
            someone links their rider to their account, so their contact details
            stay in one place.
          </p>
        </div>
        <button
          type="button"
          className="text-sm text-ink-500 hover:text-ink-800 shrink-0"
          onClick={() =>
            setPicked(
              selected.size === candidates.length
                ? new Set()
                : new Set(candidates.map((c) => c.userId)),
            )
          }
        >
          {selected.size === candidates.length ? 'Select none' : 'Select all'}
        </button>
      </div>

      {err && <p className="text-sm text-red-600 mb-2">{err}</p>}

      <ul className="divide-y divide-ink-100 max-h-80 overflow-y-auto -mx-1 px-1">
        {candidates.map((c) => (
          <li key={c.userId}>
            <label className="flex items-center gap-3 py-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.has(c.userId)}
                onChange={() => toggle(c.userId)}
                className="shrink-0"
              />
              <span className="min-w-0 flex-1">
                <span className="block font-medium truncate">{c.name || '(no name)'}</span>
                <span className="block text-xs text-ink-400 truncate">
                  {[
                    c.horseCount > 0
                      ? `${c.horseCount} horse${c.horseCount === 1 ? '' : 's'}`
                      : null,
                    c.role,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>

      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-ink-100">
        <button
          type="button"
          className="btn-primary"
          disabled={selected.size === 0 || add.isPending}
          onClick={() => add.mutate([...selected])}
        >
          {add.isPending
            ? 'Adding…'
            : `Add ${selected.size} Rider${selected.size === 1 ? '' : 's'}`}
        </button>
        <button type="button" className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function friendlyBulk(e) {
  switch (e?.body?.error || e?.message || '') {
    case 'no_users':
      return 'Pick at least one person.';
    case 'too_many':
      return 'That is more than 200 people — add them in a couple of goes.';
    default:
      return 'Could not add those. Try again.';
  }
}
